import { useCallback } from 'react';
import { endpoints } from '../services/api';
import { useSession } from '../store/session';
import { useSettings, type MediaItem } from '../store/settings';
import { usePolling } from './usePolling';

/**
 * Expands the chosen OneDrive folders into slideshow items. Each item's URL is our own
 * /api redirect, so short-lived Microsoft download links are fetched fresh when shown.
 */
export function useCloudMedia(): MediaItem[] {
  const folders = useSettings((s) => s.media.cloudFolders);
  const connected = useSession((s) => s.microsoft?.connected ?? false);
  const key = connected && folders.length ? folders.map((f) => f.folderId).join('|') : null;

  const load = useCallback(async () => {
    const results = await Promise.allSettled(folders.map((f) => endpoints.driveMedia(f.folderId)));
    return results.flatMap((r) =>
      r.status === 'fulfilled'
        ? r.value.items.map<MediaItem>((m) => ({
            id: `onedrive:${m.id}`,
            kind: m.kind,
            title: m.name,
            url: `/api/microsoft/drive/content/${encodeURIComponent(m.id)}${m.kind === 'video' ? '?kind=video' : ''}`,
          }))
        : [],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return usePolling(key, load, 30 * 60_000).data ?? NONE;
}

// Must be a stable reference: a fresh [] each render would change the media list every
// render and loop the queue's effects.
const NONE: MediaItem[] = [];
